import { Button, FormField, FormGroup, FormLabel, Loader } from '@ax/ui';
import styles from './CareCuesGroup.module.scss';
import {
  CareCuesInterface,
  CareCuesGroupItem as CareCuesGroupItemInterface,
  useAppDispatch,
  addCareCuesGroupTextResponseRequested,
  acceptCreCueItemListRequested,
  careCuesSlice,
  useAppSelector,
  noteHasPendingCareCuesResponses,
} from '@ax/redux-store';
import CareCuesGroupItem from '@/modules/CareCues/CareCuesGroupItem';
import useDraftStore, { Draft } from '@/hooks/useDraftStore';
import { REMINDER_GROUP_TYPE } from '@ax/constants';
import useAnalytics from '@/hooks/useAnalytics.tsx';
import { useState } from 'react';

export interface CareCuesGroupProps {
  noteId: string;
  group: CareCuesInterface;
  onCareCuesGroupItemDismissed: (
    item: CareCuesGroupItemInterface,
    group: CareCuesInterface,
  ) => void;
  readonly?: boolean;
}

export interface CareCuesGroupTextResponseFormProps {
  noteId: string;
  group: CareCuesInterface;
  onCareCuesGroupTextResponse: (response: string) => void;
}

function CareCuesGroupTextResponseForm({
  noteId,
  group,
  onCareCuesGroupTextResponse,
}: Readonly<CareCuesGroupTextResponseFormProps>) {
  const hasPendingCareCuesResponses = useAppSelector(noteHasPendingCareCuesResponses(noteId));
  const draft: Draft = {
    type: 'CareCueResponse',
    noteId,
    groupId: group.id,
    defaultValue: { content: '' },
  };

  const { value: responseText, setValue: setResponseText } = useDraftStore(draft);

  const handleSendResponseClick = () => {
    onCareCuesGroupTextResponse(responseText?.content ?? '');
    setResponseText(draft.defaultValue);
  };

  const isDisabled =
    hasPendingCareCuesResponses || group.items.some((item) => item.loading) || group.loading;

  return (
    <FormGroup className={styles.care_cues__group__response_form}>
      <FormField>
        <textarea
          rows={3}
          onChange={(e) => setResponseText({ content: e.target.value })}
          value={responseText?.content ?? ''}
          placeholder={'Type response here...'}
          disabled={isDisabled}
        ></textarea>
      </FormField>
      {isDisabled && hasPendingCareCuesResponses && (
        <FormLabel className="input__label--note">
          Adding new responses to this note is disabled while previous ones are being processed
        </FormLabel>
      )}
      <Button
        block={true}
        disabled={responseText?.content.length === 0 || isDisabled}
        onClick={handleSendResponseClick}
        outline={true}
      >
        {group.loading ? <Loader color="#fff" /> : 'Add to note'}
      </Button>
    </FormGroup>
  );
}

export interface CareCuesGroupCheckBoxResponseProps {
  group: CareCuesInterface;
  isEditing: boolean;
  onCareCuesGroupCheckboxResponse: (items: CareCuesGroupItemInterface[]) => void;
}

function CareCuesGroupCheckBoxResponse({
  group,
  isEditing,
  onCareCuesGroupCheckboxResponse,
}: Readonly<CareCuesGroupCheckBoxResponseProps>) {
  const handleSendResponseClick = () => {
    onCareCuesGroupCheckboxResponse(group.items);
  };

  const isDisabled = group.items.some((item) => item.loading) || group.loading || isEditing;

  return (
    <Button
      block={true}
      disabled={isDisabled}
      onClick={handleSendResponseClick}
      outline={true}
    >
      {group.loading ? <Loader color="#fff" /> : 'Add to note'}
    </Button>
  );
}

const CareCuesGroup = ({
  group,
  noteId,
  onCareCuesGroupItemDismissed,
  readonly = false,
}: CareCuesGroupProps) => {
  const analytics = useAnalytics();
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);

  const handleSendTextResponseClick = (textResponse: string, group: CareCuesInterface) => {
    dispatch(addCareCuesGroupTextResponseRequested({ noteId, response: textResponse, group }));
    analytics.onCareCuesAddToNoteButtonTapped({ noteId });
  };

  const handleSendCheckboxResponseClick = (items: CareCuesGroupItemInterface[]) => {
    dispatch(acceptCreCueItemListRequested({ noteId, items, group }));
    analytics.onCareCuesAddToNoteButtonTapped({ noteId });
  };

  const handleEditStarted = () => {
    setIsEditing(true);
  };

  const handleEditFinished = (item: CareCuesGroupItemInterface, newText: string) => {
    setIsEditing(false);
    if (newText !== item.displayText) {
      dispatch(
        careCuesSlice.actions.careCueItemTextUpdateRequested({
          item: {
            ...item,
            displayText: newText,
          },
          group,
          noteId,
        }),
      );
    }
  };

  return (
    <div
      key={group.id}
      className={styles.care_cues__group}
    >
      <h1 className={styles.care_cues__group__title}>{group.title}</h1>
      <div className={styles.care_cues__group__items}>
        {group.items.map((careCuesGroupItem) => (
          <CareCuesGroupItem
            key={`${careCuesGroupItem.id}-${careCuesGroupItem.displayTextHash}`}
            item={careCuesGroupItem}
            onDelete={(item) => onCareCuesGroupItemDismissed(item, group)}
            readonly={group.type === REMINDER_GROUP_TYPE.NEEDS_RESPONSE && readonly}
            isEditable={group.type === REMINDER_GROUP_TYPE.CHECK_BOX}
            onEditStarted={handleEditStarted}
            onEditFinished={handleEditFinished}
          />
        ))}
      </div>
      {!readonly && group.type === REMINDER_GROUP_TYPE.NEEDS_RESPONSE && (
        <CareCuesGroupTextResponseForm
          noteId={noteId}
          group={group}
          onCareCuesGroupTextResponse={(response) => handleSendTextResponseClick(response, group)}
        />
      )}
      {!readonly && group.type === REMINDER_GROUP_TYPE.CHECK_BOX && (
        <CareCuesGroupCheckBoxResponse
          group={group}
          isEditing={isEditing}
          onCareCuesGroupCheckboxResponse={handleSendCheckboxResponseClick}
        />
      )}
    </div>
  );
};

export default CareCuesGroup;
